import React, { useState, useEffect } from "react";
import { FaEdit, FaTrash, FaCheck, FaTimes } from "react-icons/fa";
import "./WeekTaskModal.css";

const WeekTaskModal = ({ weekNumber, isOpen, onClose, birthDate, onTasksUpdate }) => {
  const storageKey = `week_tasks_${weekNumber}`;

  // Initialize tasks from localStorage immediately (like GoalTracker does)
  const [tasks, setTasks] = useState(() => {
    try {
      const savedTasks = localStorage.getItem(storageKey);
      return savedTasks ? JSON.parse(savedTasks) : [];
    } catch (e) {
      console.error('Error loading tasks from localStorage:', e);
      return [];
    }
  });

  const [newTask, setNewTask] = useState("");
  const [editIndex, setEditIndex] = useState(null);
  const [editValue, setEditValue] = useState("");

  // Reload tasks from localStorage when weekNumber changes
  useEffect(() => {
    try {
      const savedTasks = localStorage.getItem(storageKey);
      if (savedTasks) {
        const parsedTasks = JSON.parse(savedTasks);
        // Ensure we have a valid array
        if (Array.isArray(parsedTasks)) {
          setTasks(parsedTasks);
        } else {
          setTasks([]);
        }
      } else {
        setTasks([]);
      }
    } catch (e) {
      console.error('Error loading tasks from localStorage:', e);
      setTasks([]);
    }
  }, [weekNumber, storageKey]);

  // Save tasks to localStorage whenever tasks change - ALWAYS save (like GoalTracker)
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(tasks));
      // Notify parent component to refresh task indicators
      if (onTasksUpdate) {
        onTasksUpdate(weekNumber, tasks.length > 0);
      }
    } catch (e) {
      console.error('Error saving tasks to localStorage:', e);
    }
  }, [tasks, storageKey, weekNumber, onTasksUpdate]);

  // Calculate week date range
  const getWeekDateRange = () => {
    if (!birthDate || isNaN(birthDate.getTime())) {
      return { start: "N/A", end: "N/A" };
    }
    const startDate = new Date(birthDate);
    startDate.setDate(startDate.getDate() + weekNumber * 7);
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 6);

    return {
      start: startDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
      end: endDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
    };
  };

  const dateRange = getWeekDateRange();

  const handleAddTask = (e) => {
    e.preventDefault();
    if (newTask.trim() !== "") {
      setTasks([...tasks, { text: newTask.trim(), completed: false, id: Date.now() }]);
      setNewTask("");
    }
  };

  const handleDeleteTask = (id) => {
    // Only delete if user confirms (optional - you can remove confirmation if you want instant delete)
    const updatedTasks = tasks.filter((task) => task.id !== id);
    setTasks(updatedTasks);
    // Tasks will be automatically saved to localStorage via useEffect
  };

  const handleToggleComplete = (id) => {
    setTasks(
      tasks.map((task) => (task.id === id ? { ...task, completed: !task.completed } : task))
    );
  };

  const handleEditTask = (id) => {
    const task = tasks.find((t) => t.id === id);
    if (task) {
      setEditIndex(id);
      setEditValue(task.text);
    }
  };

  const handleSaveEdit = (id) => {
    if (editValue.trim() !== "") {
      setTasks(
        tasks.map((task) => (task.id === id ? { ...task, text: editValue.trim() } : task))
      );
      setEditIndex(null);
      setEditValue("");
    }
  };

  const handleCancelEdit = () => {
    setEditIndex(null);
    setEditValue("");
  };

  if (!isOpen) return null;

  const completedCount = tasks.filter((t) => t.completed).length;
  const totalTasks = tasks.length;
  const progressPercentage = totalTasks > 0 ? (completedCount / totalTasks) * 100 : 0;

  return (
    <div className="week-task-modal-overlay" onClick={onClose}>
      <div className="week-task-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="week-task-modal-header">
          <div>
            <h2 className="week-task-modal-title">Week {weekNumber + 1} Tasks</h2>
            <p className="week-task-modal-date">
              {dateRange.start} - {dateRange.end}
            </p>
          </div>
          <button className="week-task-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* Progress Bar */}
        {totalTasks > 0 && (
          <div className="week-task-progress-container">
            <div className="week-task-progress-bar">
              <div
                className="week-task-progress-fill"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
            <p className="week-task-progress-text">
              {completedCount} of {totalTasks} tasks completed ({Math.round(progressPercentage)}%)
            </p>
          </div>
        )}

        {/* Task Form */}
        <form onSubmit={handleAddTask} className="week-task-form">
          <input
            type="text"
            placeholder="Add a task for this week..."
            value={newTask}
            onChange={(e) => setNewTask(e.target.value)}
            className="week-task-input"
          />
          <button type="submit" className="week-task-add-btn">
            <FaCheck /> Add
          </button>
        </form>

        {/* Tasks List */}
        <div className="week-task-list">
          {tasks.length === 0 ? (
            <div className="week-task-empty">
              <p>No tasks yet. Add your first task above! ✨</p>
            </div>
          ) : (
            tasks.map((task) => (
              <div
                key={task.id}
                className={`week-task-item ${task.completed ? "completed" : ""}`}
              >
                {editIndex === task.id ? (
                  <div className="week-task-edit-mode">
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="week-task-edit-input"
                      autoFocus
                    />
                    <div className="week-task-edit-actions">
                      <button
                        onClick={() => handleSaveEdit(task.id)}
                        className="week-task-save-btn"
                      >
                        <FaCheck />
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="week-task-cancel-btn"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="week-task-content">
                      <button
                        onClick={() => handleToggleComplete(task.id)}
                        className={`week-task-checkbox ${task.completed ? "checked" : ""}`}
                      >
                        {task.completed && <FaCheck />}
                      </button>
                      <span className="week-task-text">{task.text}</span>
                    </div>
                    <div className="week-task-actions">
                      <button
                        onClick={() => handleEditTask(task.id)}
                        className="week-task-edit-btn"
                        title="Edit task"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDeleteTask(task.id)}
                        className="week-task-delete-btn"
                        title="Delete task"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default WeekTaskModal;

